import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles, FolderKanban, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import ModelSelector from "@/components/ModelSelector";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", model_id: "openai:gpt-5.6-terra" });
  const nav = useNavigate();

  useEffect(() => {
    http.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await http.post("/projects", form);
      toast.success("Project created");
      setOpen(false);
      nav(`/build/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Your workspace</p>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold text-slate-900">
            Welcome back, {user?.full_name?.split(" ")[0]}.
          </h1>
          <p className="mt-2 text-slate-600">Pick up a project, or scaffold something new.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-new-project">
              <Plus className="h-4 w-4 mr-2" /> New project
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-project-dialog">
            <DialogHeader><DialogTitle className="font-display">Scaffold a new app</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <label className="text-sm font-medium">Project title</label>
                <Input required data-testid="input-project-title"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Describe the healthcare app you want to build</label>
                <Textarea rows={4} required data-testid="input-project-description"
                  placeholder="e.g. A patient portal for a small pediatric clinic with vaccine reminders and secure messaging"
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Choose your model</span>
                <ModelSelector value={form.model_id} onChange={(id) => setForm({ ...form, model_id: id })} compact />
              </div>
              <Button type="submit" className="w-full bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-create-project">
                <Sparkles className="h-4 w-4 mr-2" /> Create & open builder
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-2 text-slate-700">
          <FolderKanban className="h-4 w-4" />
          <h2 className="font-display font-semibold">Projects</h2>
          <span className="text-xs font-mono text-slate-500">{projects.length} total</span>
        </div>

        {projects.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="empty-projects">
            <Sparkles className="h-6 w-6 text-emerald-700 mx-auto" />
            <h3 className="mt-3 font-display font-semibold text-slate-900">No projects yet</h3>
            <p className="mt-1 text-sm text-slate-600">Start from a template or open a blank builder.</p>
            <div className="mt-5 flex justify-center gap-3">
              <Link to="/templates"><Button variant="outline" data-testid="btn-empty-templates">Browse templates</Button></Link>
              <Button onClick={() => setOpen(true)} className="bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-empty-new">
                <Plus className="h-4 w-4 mr-2" /> New project
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {projects.map((p) => (
              <Link key={p.id} to={`/build/${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-400 transition-colors" data-testid={`project-card-${p.id}`}>
                <h3 className="font-display font-semibold text-slate-900">{p.title}</h3>
                <p className="mt-1 text-sm text-slate-600 line-clamp-2">{p.description}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-mono">{p.model_id.split(":")[0]}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(p.updated_at).toLocaleDateString()}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
